import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";

// Sprint 8.A.2 — Animierte Timeline mit Tag-Filter (Hybrid-Variante C).
// Flexpfade als SVG-Punkte, chronologisch (älteste links, neueste rechts).
// Animation B (Mittel): Sequenz-Aufbau 150ms/Punkt, Hover-Pulse, Verbindungslinie.

const VIEW_W = 800;
const VIEW_H = 180;
const PADDING_X = 30;
const Y_BASE = 140;

function buildPath(points) {
  if (points.length < 2) return "";
  const segs = points.map((p, i) => {
    const cmd = i === 0 ? "M" : "L";
    return `${cmd}${p.cx.toFixed(1)},${p.cy.toFixed(1)}`;
  });
  return segs.join(" ");
}

export default function CockpitTimeline({ data }) {
  const [selectedTag, setSelectedTag] = useState("alle");
  const [hoverId, setHoverId] = useState(null);

  // Post-Launch-H4 (10.02.2026) — bruecken/available_tags in useMemo wrappen,
  // damit die abhaengigen useMemo-Hooks (filtered/points) nicht bei jedem
  // Render referenzgleich neu ausgeloest werden. Fixed ESLint-Deploy-Block.
  const bruecken = useMemo(() => data?.bruecken || [], [data]);
  const availableTags = useMemo(() => data?.available_tags || [], [data]);

  const filtered = useMemo(() => {
    if (selectedTag === "alle") return bruecken;
    return bruecken.filter((b) => (b.tags || []).includes(selectedTag));
  }, [bruecken, selectedTag]);

  const points = useMemo(() => {
    if (filtered.length === 0) return [];
    const n = filtered.length;
    const usableW = VIEW_W - PADDING_X * 2;
    const step = n > 1 ? usableW / (n - 1) : 0;
    return filtered.map((b, i) => ({
      ...b,
      cx: PADDING_X + (n > 1 ? i * step : usableW / 2),
      cy: Y_BASE - b.importance * 100,
      r: Math.max(4, b.importance * 10),
    }));
  }, [filtered]);

  return (
    <div data-testid="cockpit-timeline" className="cockpit-timeline">
      {/* Tag-Filter — Hybrid C */}
      <div
        data-testid="timeline-tag-filter"
        className="mb-3 flex flex-wrap gap-1.5"
      >
        <button
          type="button"
          onClick={() => setSelectedTag("alle")}
          data-testid="timeline-tag-alle"
          className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${
            selectedTag === "alle"
              ? "bg-[#4563BB] text-white"
              : "bg-slate-100 text-slate-700 hover:bg-slate-200"
          }`}
        >
          Alle ({bruecken.length})
        </button>
        {availableTags.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setSelectedTag(t)}
            data-testid={`timeline-tag-${t}`}
            className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition-colors ${
              selectedTag === t
                ? "bg-[#4563BB] text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* SVG */}
      {points.length === 0 ? (
        <p
          data-testid="timeline-empty"
          className="text-sm text-slate-500 italic py-8 text-center"
        >
          {selectedTag === "alle"
            ? "Noch keine Flexpfade — leg deinen ersten an."
            : `Keine Flexpfade mit Tag '${selectedTag}'.`}
        </p>
      ) : (
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          className="w-full"
          style={{ maxHeight: 220 }}
          role="img"
          aria-label="Flexpfad-Timeline"
        >
          <defs>
            <linearGradient id="timeline-gradient" x1="0%" x2="100%">
              <stop offset="0%" stopColor="#4563BB" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#4563BB" stopOpacity="1" />
            </linearGradient>
          </defs>

          {/* X-Achsen-Linie */}
          <line
            x1={PADDING_X}
            x2={VIEW_W - PADDING_X}
            y1={Y_BASE + 20}
            y2={Y_BASE + 20}
            stroke="#E2E8F0"
            strokeWidth="1"
          />

          {/* Verbindungs-Pfad */}
          {points.length >= 2 && (
            <motion.path
              d={buildPath(points)}
              stroke="url(#timeline-gradient)"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.3 }}
            />
          )}

          {/* Punkte */}
          {points.map((p, idx) => (
            <motion.g
              key={p.run_id || idx}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                delay: idx * 0.15,
                type: "spring",
                stiffness: 200,
                damping: 12,
              }}
              onMouseEnter={() => setHoverId(p.run_id)}
              onMouseLeave={() => setHoverId(null)}
              style={{ cursor: "pointer" }}
            >
              <motion.circle
                cx={p.cx}
                cy={p.cy}
                r={p.r}
                fill="#4563BB"
                fillOpacity={0.85}
                stroke="white"
                strokeWidth="2"
                data-testid={`timeline-point-${p.run_id}`}
                whileHover={{ scale: 1.4 }}
              />
              {hoverId === p.run_id && (
                <>
                  <rect
                    x={Math.min(VIEW_W - 220, Math.max(0, p.cx - 110))}
                    y={Math.max(0, p.cy - 50)}
                    width="220"
                    height="38"
                    rx="6"
                    fill="white"
                    stroke="#CBD5E1"
                    strokeWidth="1"
                  />
                  <text
                    x={Math.min(VIEW_W - 220, Math.max(0, p.cx - 110)) + 10}
                    y={Math.max(0, p.cy - 32)}
                    fontSize="11"
                    fill="#0F172A"
                    fontWeight="600"
                  >
                    {p.title.length > 35 ? p.title.slice(0, 33) + "…" : p.title}
                  </text>
                  <text
                    x={Math.min(VIEW_W - 220, Math.max(0, p.cx - 110)) + 10}
                    y={Math.max(0, p.cy - 18)}
                    fontSize="10"
                    fill="#64748B"
                  >
                    {(p.date || "").slice(0, 10)}
                    {(p.tags || []).length > 0 ? " · " + p.tags.slice(0, 2).join(", ") : ""}
                  </text>
                </>
              )}
            </motion.g>
          ))}
        </svg>
      )}
    </div>
  );
}
